import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { SignupModalProvider } from "@/contexts/SignupModalContext";
import ScrollToTop from "@/components/ScrollToTop";
import { useSignupModal } from "@/contexts/SignupModalContext";
import ErrorBoundary from "./components/ErrorBoundary";
import { lazy, Suspense } from "react";

// Eager: homepage + NotFound only. Everything else is code-split so the initial
// bundle only contains what's needed for the LCP paint on "/".
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";

// Core pages
const Features = lazy(() => import("./pages/Features"));
const Industries = lazy(() => import("./pages/Industries"));
const Pricing = lazy(() => import("./pages/Pricing"));
const Contact = lazy(() => import("./pages/Contact"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const About = lazy(() => import("./pages/About"));
const Solutions = lazy(() => import("./pages/Solutions"));
const WhyFlip = lazy(() => import("./pages/WhyFlip"));
const Admin = lazy(() => import("./pages/Admin"));
const Resources = lazy(() => import("./pages/Resources"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const FAQ = lazy(() => import("./pages/FAQ"));

// Solution Pages
const CrossBorderPayments = lazy(() => import("./pages/solutions/CrossBorderPayments"));
const FreelancerPayments = lazy(() => import("./pages/solutions/FreelancerPayments"));
const BulkPayments = lazy(() => import("./pages/solutions/BulkPayments"));
const ApiIntegration = lazy(() => import("./pages/solutions/ApiIntegration"));
const StablecoinPayroll = lazy(() => import("./pages/solutions/StablecoinPayroll"));
const PayEmployeesInCrypto = lazy(() => import("./pages/solutions/PayEmployeesInCrypto"));
const PayContractorsCountry = lazy(() => import("./pages/solutions/PayContractorsCountry"));
const CryptoInvoicing = lazy(() => import("./pages/solutions/CryptoInvoicing"));

// Industry Pages
const FreelancerPlatforms = lazy(() => import("./pages/industries/FreelancerPlatforms"));
const TechStartups = lazy(() => import("./pages/industries/TechStartups"));
const CreativeStudios = lazy(() => import("./pages/industries/CreativeStudios"));
const ECommerce = lazy(() => import("./pages/industries/ECommerce"));
const RecruitmentAgencies = lazy(() => import("./pages/industries/RecruitmentAgencies"));
const Consulting = lazy(() => import("./pages/industries/Consulting"));

// UAE Pages
const UAEHub = lazy(() => import("./pages/uae/UAEHub"));
const UAEResources = lazy(() => import("./pages/uae/UAEResources"));
const FXLeakChecker = lazy(() => import("./pages/uae/FXLeakChecker"));
const CostSavingsEstimatePage = lazy(() => import("./pages/uae/CostSavingsEstimatePage"));
const UAECorridorIndia = lazy(() => import("./pages/uae/UAECorridorIndia"));
const UAECorridorPakistan = lazy(() => import("./pages/uae/UAECorridorPakistan"));
const UAECorridorPhilippines = lazy(() => import("./pages/uae/UAECorridorPhilippines"));
const UAECorridorUKEurope = lazy(() => import("./pages/uae/UAECorridorUKEurope"));
const UAEFAQ = lazy(() => import("./pages/uae/UAEFAQ"));
const UAEContractorPayments = lazy(() => import("./pages/uae/UAEContractorPayments"));
const UAETransferFees = lazy(() => import("./pages/uae/UAETransferFees"));
const PayoutChecklist = lazy(() => import("./pages/uae/PayoutChecklist"));
const BankDeductionsExplainer = lazy(() => import("./pages/uae/BankDeductionsExplainer"));
const UAEPayContractors = lazy(() => import("./pages/uae/UAEPayContractors"));
const UAEPayFreelancers = lazy(() => import("./pages/uae/UAEPayFreelancers"));
const UAEBulkPayouts = lazy(() => import("./pages/uae/UAEBulkPayouts"));
const PayContractorsIn = lazy(() => import("./pages/uae/PayContractorsIn"));
const BankFeesPage = lazy(() => import("./pages/uae/BankFeesPage"));

// AE Landing Pages
const FreelancerPaymentsAE = lazy(() => import("./pages/ae/FreelancerPaymentsAE"));
const CryptoPayrollAE = lazy(() => import("./pages/ae/CryptoPayrollAE"));
const USDTToBankAE = lazy(() => import("./pages/ae/USDTToBankAE"));
const MassPayoutsAE = lazy(() => import("./pages/ae/MassPayoutsAE"));
const CryptoWalletUAE = lazy(() => import("./pages/ae/CryptoWalletUAE"));
const USDTToAED = lazy(() => import("./pages/ae/USDTToAED"));
const AEDToINR = lazy(() => import("./pages/ae/AEDToINR"));
const AEDToPKR = lazy(() => import("./pages/ae/AEDToPKR"));
const AEDToPHP = lazy(() => import("./pages/ae/AEDToPHP"));
const AEDToEUR = lazy(() => import("./pages/ae/AEDToEUR"));

// Comparison Pages
const FlipVsBanks = lazy(() => import("./pages/compare/FlipVsBanks"));
const FlipVsPaypal = lazy(() => import("./pages/compare/FlipVsPaypal"));
const FlipVsCompetitor = lazy(() => import("./pages/compare/FlipVsCompetitor"));
const BitwageAlternative = lazy(() => import("./pages/alternatives/BitwageAlternative"));
const BitwageVsRiseVsFlip = lazy(() => import("./pages/compare/BitwageVsRiseVsFlip"));
const CryptoLegalityGuide = lazy(() => import("./pages/guides/CryptoLegalityGuide"));
const CurrencyConverter = lazy(() => import("./pages/tools/CurrencyConverter"));
const ToolsHub = lazy(() => import("./pages/tools/ToolsHub"));
const UsdcHub = lazy(() => import("./pages/stablecoins/UsdcHub"));


// Conversion Pages
const BookDemo = lazy(() => import("./pages/BookDemo"));
const EORPayrollPlatforms = lazy(() => import("./pages/EORPayrollPlatforms"));
const Enterprise = lazy(() => import("./pages/Enterprise"));
const Unsubscribe = lazy(() => import("./pages/Unsubscribe"));
const Signup = lazy(() => import("./pages/Signup"));
const XLanding = lazy(() => import("./pages/lp/XLanding"));
const BusinessPayments = lazy(() => import("./pages/BusinessPayments"));
const PaymentAppsHub = lazy(() => import("./pages/payment-apps/PaymentAppsHub"));
const PaymentAppsCountry = lazy(() => import("./pages/payment-apps/PaymentAppsCountry"));
const StablecoinBusinessPaymentsHub = lazy(() => import("./pages/stablecoin-business-payments/StablecoinBusinessPaymentsHub"));
const StablecoinBusinessPaymentsCountry = lazy(() => import("./pages/stablecoin-business-payments/StablecoinBusinessPaymentsCountry"));
const EuropeHub = lazy(() => import("./pages/europe/EuropeHub"));
const EuropeCountry = lazy(() => import("./pages/europe/EuropeCountry"));

const ChatWidget = lazy(() => import("./components/ChatWidget"));
const SignupCaptureModal = lazy(() => import("@/components/SignupCaptureModal"));

// Only download the signup modal once someone opens it.
const LazySignupModal = () => {
  const { isOpen } = useSignupModal();
  if (!isOpen) return null;
  return <Suspense fallback={null}><SignupCaptureModal /></Suspense>;
};

const queryClient = new QueryClient();

const AppRoutes = () => (
  <Routes>
    {/* English routes (default) */}
    <Route path="/" element={<Index />} />
    <Route path="/features" element={<Features />} />
    <Route path="/industries" element={<Industries />} />
    <Route path="/pricing" element={<Pricing />} />
    <Route path="/contact" element={<Contact />} />
    <Route path="/blog" element={<Blog />} />
    <Route path="/blog/:slug" element={<BlogPost />} />
    <Route path="/about" element={<About />} />
    <Route path="/solutions" element={<Solutions />} />
    <Route path="/why-flip" element={<WhyFlip />} />
    <Route path="/admin" element={<Admin />} />
    <Route path="/resources" element={<Resources />} />

    {/* Solution Pages */}
    <Route path="/solutions/cross-border-payments" element={<CrossBorderPayments />} />
    <Route path="/solutions/freelancer-payments" element={<FreelancerPayments />} />
    <Route path="/solutions/bulk-payments" element={<BulkPayments />} />
    <Route path="/solutions/api-integration" element={<ApiIntegration />} />
    <Route path="/solutions/stablecoin-payroll" element={<StablecoinPayroll />} />
    <Route path="/solutions/pay-employees-in-crypto" element={<PayEmployeesInCrypto />} />
    <Route path="/solutions/crypto-invoicing" element={<CryptoInvoicing />} />
    <Route path="/ar/solutions/pay-employees-in-crypto" element={<PayEmployeesInCrypto />} />
    <Route path="/solutions/pay-contractors/:countrySlug" element={<PayContractorsCountry />} />
    <Route path="/ar/solutions/pay-contractors/:countrySlug" element={<PayContractorsCountry />} />

    {/* Industry Pages */}
    <Route path="/industries/freelancer-platforms" element={<FreelancerPlatforms />} />
    <Route path="/industries/tech-startups" element={<TechStartups />} />
    <Route path="/industries/creative-studios" element={<CreativeStudios />} />
    <Route path="/industries/e-commerce" element={<ECommerce />} />
    <Route path="/industries/recruitment-agencies" element={<RecruitmentAgencies />} />
    <Route path="/industries/consulting" element={<Consulting />} />

    {/* UAE Hub Routes */}
    <Route path="/uae" element={<UAEHub />} />
    <Route path="/uae/resources" element={<UAEResources />} />
    <Route path="/uae/tools/fx-leak-checker" element={<FXLeakChecker />} />
    <Route path="/uae/tools/cost-savings-estimate" element={<CostSavingsEstimatePage />} />
    <Route path="/uae/send-money/india" element={<UAECorridorIndia />} />
    <Route path="/uae/send-money/pakistan" element={<UAECorridorPakistan />} />
    <Route path="/uae/send-money/philippines" element={<UAECorridorPhilippines />} />
    <Route path="/uae/send-money/uk-europe" element={<UAECorridorUKEurope />} />
    <Route path="/uae/faq" element={<UAEFAQ />} />
    <Route path="/uae/contractor-payments" element={<UAEContractorPayments />} />
    <Route path="/uae/transfer-fees-fx" element={<UAETransferFees />} />
    <Route path="/uae/resources/contractor-payout-checklist" element={<PayoutChecklist />} />
    <Route path="/uae/resources/bank-deductions-explainer" element={<BankDeductionsExplainer />} />
    <Route path="/uae/pay-contractors" element={<UAEPayContractors />} />
    <Route path="/uae/pay-freelancers" element={<UAEPayFreelancers />} />
    <Route path="/uae/bulk-payouts" element={<UAEBulkPayouts />} />
    <Route path="/uae/pay-contractors-in/:countrySlug" element={<PayContractorsIn />} />
    <Route path="/uae/bank-fees/:bankSlug" element={<BankFeesPage />} />

    {/* AE Landing Pages */}
    <Route path="/ae/freelancer-payments" element={<FreelancerPaymentsAE />} />
    <Route path="/ae/crypto-payroll" element={<CryptoPayrollAE />} />
    <Route path="/ae/usdt-to-bank" element={<USDTToBankAE />} />
    <Route path="/ae/mass-payouts" element={<MassPayoutsAE />} />
    <Route path="/ae/crypto-wallet-uae" element={<CryptoWalletUAE />} />
    <Route path="/ae/usdt-to-aed" element={<USDTToAED />} />
    <Route path="/ae/aed-to-inr" element={<AEDToINR />} />
    <Route path="/ae/aed-to-pkr" element={<AEDToPKR />} />
    <Route path="/ae/aed-to-php" element={<AEDToPHP />} />
    <Route path="/ae/aed-to-eur" element={<AEDToEUR />} />

    {/* Comparison Pages */}
    <Route path="/compare/flip-vs-banks" element={<FlipVsBanks />} />
    <Route path="/compare/flip-vs-paypal" element={<FlipVsPaypal />} />
    <Route path="/compare/bitwage-vs-rise-vs-flip" element={<BitwageVsRiseVsFlip />} />
    <Route path="/compare/:competitorSlug" element={<FlipVsCompetitor />} />
    <Route path="/alternatives/bitwage-alternative" element={<BitwageAlternative />} />
    <Route path="/ar/alternatives/bitwage-alternative" element={<BitwageAlternative />} />

    {/* Crypto Legality Guides */}
    <Route path="/guides/:guideSlug" element={<CryptoLegalityGuide />} />

    {/* Stablecoin hubs */}
    <Route path="/stablecoins/usdc" element={<UsdcHub />} />


    {/* Conversion Tools */}
    <Route path="/tools" element={<ToolsHub />} />
    <Route path="/tools/:pairSlug" element={<CurrencyConverter />} />

    {/* Conversion Pages */}
    <Route path="/book-demo" element={<BookDemo />} />
    <Route path="/signup" element={<Signup />} />
    <Route path="/eor-payroll-platforms" element={<EORPayrollPlatforms />} />
    <Route path="/enterprise" element={<Enterprise />} />
    <Route path="/fees" element={<Pricing />} />
    <Route path="/lp/x" element={<XLanding />} />
    <Route path="/lp/business-payouts" element={<XLanding />} />
    <Route path="/business-payments" element={<BusinessPayments />} />
    <Route path="/payment-apps" element={<PaymentAppsHub />} />
    <Route path="/payment-apps/:countrySlug" element={<PaymentAppsCountry />} />
    <Route path="/stablecoin-business-payments" element={<StablecoinBusinessPaymentsHub />} />
    <Route path="/stablecoin-business-payments/:countrySlug" element={<StablecoinBusinessPaymentsCountry />} />
    <Route path="/europe" element={<EuropeHub />} />
    <Route path="/europe/:countrySlug" element={<EuropeCountry />} />

    <Route path="/privacy" element={<PrivacyPolicy />} />
    <Route path="/terms" element={<TermsOfService />} />
    <Route path="/faq" element={<FAQ />} />
    <Route path="/unsubscribe" element={<Unsubscribe />} />

    {/* Arabic routes with /ar prefix */}
    <Route path="/ar" element={<Index />} />
    <Route path="/ar/features" element={<Features />} />
    <Route path="/ar/industries" element={<Industries />} />
    <Route path="/ar/pricing" element={<Pricing />} />
    <Route path="/ar/contact" element={<Contact />} />
    <Route path="/ar/blog" element={<Blog />} />
    <Route path="/ar/blog/:slug" element={<BlogPost />} />
    <Route path="/ar/about" element={<About />} />
    <Route path="/ar/solutions" element={<Solutions />} />
    <Route path="/ar/why-flip" element={<WhyFlip />} />
    <Route path="/ar/resources" element={<Resources />} />

    {/* Arabic Solution Pages */}
    <Route path="/ar/solutions/cross-border-payments" element={<CrossBorderPayments />} />
    <Route path="/ar/solutions/freelancer-payments" element={<FreelancerPayments />} />
    <Route path="/ar/solutions/bulk-payments" element={<BulkPayments />} />
    <Route path="/ar/solutions/api-integration" element={<ApiIntegration />} />

    {/* Arabic Industry Pages */}
    <Route path="/ar/industries/freelancer-platforms" element={<FreelancerPlatforms />} />
    <Route path="/ar/industries/tech-startups" element={<TechStartups />} />
    <Route path="/ar/industries/creative-studios" element={<CreativeStudios />} />
    <Route path="/ar/industries/e-commerce" element={<ECommerce />} />
    <Route path="/ar/industries/recruitment-agencies" element={<RecruitmentAgencies />} />
    <Route path="/ar/industries/consulting" element={<Consulting />} />

    {/* Arabic UAE Hub Routes */}
    <Route path="/ar/uae" element={<UAEHub />} />
    <Route path="/ar/uae/resources" element={<UAEResources />} />
    <Route path="/ar/uae/tools/fx-leak-checker" element={<FXLeakChecker />} />
    <Route path="/ar/uae/tools/cost-savings-estimate" element={<CostSavingsEstimatePage />} />
    <Route path="/ar/uae/send-money/india" element={<UAECorridorIndia />} />
    <Route path="/ar/uae/send-money/pakistan" element={<UAECorridorPakistan />} />
    <Route path="/ar/uae/send-money/philippines" element={<UAECorridorPhilippines />} />
    <Route path="/ar/uae/send-money/uk-europe" element={<UAECorridorUKEurope />} />
    <Route path="/ar/uae/faq" element={<UAEFAQ />} />
    <Route path="/ar/uae/contractor-payments" element={<UAEContractorPayments />} />
    <Route path="/ar/uae/transfer-fees-fx" element={<UAETransferFees />} />
    <Route path="/ar/uae/resources/contractor-payout-checklist" element={<PayoutChecklist />} />
    <Route path="/ar/uae/resources/bank-deductions-explainer" element={<BankDeductionsExplainer />} />
    <Route path="/ar/uae/pay-contractors" element={<UAEPayContractors />} />
    <Route path="/ar/uae/pay-freelancers" element={<UAEPayFreelancers />} />
    <Route path="/ar/uae/bulk-payouts" element={<UAEBulkPayouts />} />
    <Route path="/ar/uae/pay-contractors-in/:countrySlug" element={<PayContractorsIn />} />
    <Route path="/ar/uae/bank-fees/:bankSlug" element={<BankFeesPage />} />

    {/* Arabic AE Pages */}
    <Route path="/ar/ae/freelancer-payments" element={<FreelancerPaymentsAE />} />
    <Route path="/ar/ae/usdt-to-aed" element={<USDTToAED />} />
    <Route path="/ar/ae/aed-to-inr" element={<AEDToINR />} />
    <Route path="/ar/ae/aed-to-pkr" element={<AEDToPKR />} />
    <Route path="/ar/ae/aed-to-php" element={<AEDToPHP />} />
    <Route path="/ar/ae/aed-to-eur" element={<AEDToEUR />} />

    <Route path="/ar/compare/:competitorSlug" element={<FlipVsCompetitor />} />
    <Route path="/ar/guides/:guideSlug" element={<CryptoLegalityGuide />} />

    {/* Arabic Conversion Pages */}
    <Route path="/ar/book-demo" element={<BookDemo />} />
    <Route path="/ar/signup" element={<Signup />} />
    <Route path="/ar/fees" element={<Pricing />} />

    <Route path="/ar/privacy" element={<PrivacyPolicy />} />
    <Route path="/ar/terms" element={<TermsOfService />} />
    <Route path="/ar/faq" element={<FAQ />} />

    {/* Legacy URL Redirects */}
    <Route path="/uae/corridor/india" element={<Navigate to="/uae/send-money/india" replace />} />
    <Route path="/uae/corridor/pakistan" element={<Navigate to="/uae/send-money/pakistan" replace />} />
    <Route path="/uae/corridor/philippines" element={<Navigate to="/uae/send-money/philippines" replace />} />
    <Route path="/uae/corridor/uk-europe" element={<Navigate to="/uae/send-money/uk-europe" replace />} />
    <Route path="/uae/guides/payout-checklist" element={<Navigate to="/uae/resources/contractor-payout-checklist" replace />} />
    <Route path="/uae/guides/bank-deductions-explainer" element={<Navigate to="/uae/resources/bank-deductions-explainer" replace />} />
    <Route path="/uae/transfer-fees" element={<Navigate to="/uae/transfer-fees-fx" replace />} />
    <Route path="/privacy-policy" element={<Navigate to="/privacy" replace />} />
    <Route path="/terms-of-service" element={<Navigate to="/terms" replace />} />

    <Route path="*" element={<NotFound />} />
  </Routes>
);

const App = () => (
  <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <BrowserRouter>
          <LanguageProvider>
            <SignupModalProvider>
              <Toaster />
              <Sonner />
              <ScrollToTop />
              <Suspense fallback={null}>
                <AppRoutes />
              </Suspense>
              <Suspense fallback={null}><ChatWidget /></Suspense>
              <LazySignupModal />
            </SignupModalProvider>
          </LanguageProvider>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </ErrorBoundary>
);

export default App;
